import { useTranslation } from 'next-i18next';
import React from 'react';

import ButtonBase from './button-base';

type ButtonProps = Omit<React.HTMLProps<HTMLButtonElement>, 'type'>;
interface Props extends ButtonProps {
  btnText: string;
  className: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  onClick: () => void;
  type?: 'button' | 'reset' | 'submit';
  value?: string;
}

/**
 * Returns an instance of a DoneButton.
 *
 * @param btnText - Contents of the button. If not set, "Done" will be used.
 * @param className - Class name(s) to apply.
 * @param disabled - If true, sets `disabled` property on the button.
 * @param id - The value to set for the `id` property.
 * @param name - The value to set for the `name` property.
 * @param onClick - The callback handler for the click event.
 * @param type - Type property. Must be `button`, `reset`, or `submit`.
 * @param value - Value to set on the button.
 * @param props - Customization options passed to base button.
 * @returns React.ReactNode.
 *
 * @example
 *
 * ```typescriptreact
 * <DoneButton
 *   btnText="Done"
 *   onClick={() => {}
 * />
 * ```
 */
export function DoneButton({ btnText, className, disabled, id, name, onClick, type, value, ...props }: Props) {
  const { t } = useTranslation('common');

  return (
    <ButtonBase
      className={`cta small secondary ${className ? `${className}` : ''}`}
      disabled={disabled}
      id={id}
      name={name}
      onClick={onClick}
      title={btnText || t('giving.cta.done')}
      type={type}
      value={value}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {btnText || t('giving.cta.done')}
    </ButtonBase>
  );
}
